(() => {
  'use strict';

  if (window.__SOLI_PRODUCT_CURRENCY_LOCALIZER__) return;
  window.__SOLI_PRODUCT_CURRENCY_LOCALIZER__ = true;

  const TARGET_SELECTOR = [
    '.product__info-container [id^="price-"]',
    '.product__info-container .price',
    '.leadform-container',
    '.leadform-submit-form',
    '.leadform-delivery-container',
    '.leadform-bundles',
    '[data-leadform-id]',
  ].join(',');

  const amount = '([0-9]+(?:[,.\\u00a0\\u202f ]?[0-9]{3})*(?:[,.][0-9]{1,2})?)';
  const prefixedCurrency = new RegExp('(?:DZD|DA|دج)\\s*' + amount, 'g');
  const suffixedCurrency = new RegExp(amount + '\\s*(?:DZD|DA|دج)', 'g');
  const currencyPattern = /\b(?:DZD|DA)\b|دج/g;
  const currencyForLocale = () =>
    document.documentElement.lang.toLowerCase().startsWith('ar') ? 'دج' : 'DA';

  const formatPrice = (value) => {
    const currency = currencyForLocale();
    return value
      .replace(prefixedCurrency, (_match, price) => `${price.trim()} ${currency}`)
      .replace(suffixedCurrency, (_match, price) => `${price.trim()} ${currency}`)
      .replace(currencyPattern, currency);
  };

  const replaceTextNode = (node) => {
    if (node.nodeType !== Node.TEXT_NODE || !node.nodeValue) return;
    const parent = node.parentElement;
    if (!parent || parent.closest('script, style, noscript, textarea')) return;
    const formatted = formatPrice(node.nodeValue);
    if (formatted !== node.nodeValue) node.nodeValue = formatted;
  };

  const replaceWithin = (root) => {
    if (root.nodeType === Node.TEXT_NODE) {
      replaceTextNode(root);
      return;
    }
    if (!(root instanceof Element)) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      replaceTextNode(node);
      node = walker.nextNode();
    }
  };

  const localizeNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.parentElement?.closest(TARGET_SELECTOR)) replaceTextNode(node);
      return;
    }
    if (!(node instanceof Element)) return;

    if (node.matches(TARGET_SELECTOR) || node.closest(TARGET_SELECTOR)) {
      replaceWithin(node);
      return;
    }

    node.querySelectorAll(TARGET_SELECTOR).forEach(replaceWithin);
  };

  /*
   * Synchronise le prix principal Dawn avec le pack Webi actif.
   * L'application reste la source de vérité : le prix est lu directement
   * dans le ruban du pack sélectionné, après que Webi l'a rendu.
   */
  const BUNDLE_ROOT_SELECTOR = '.leadform-bundles, [data-element-id="bundles"]';
  const ACTIVE_BUNDLE_SELECTOR = [
    '.lfcod-bundle-item.active',
    '.lfcod-bundle-item.is-active',
    '.lfcod-bundle-item.selected',
    '.lfcod-bundle-item[aria-selected="true"]',
  ].join(',');
  const BUNDLE_PRICE_SELECTOR = [
    '.ribbon-bundel-r',
    '.ribbon-bundel-l',
    '[data-bundle-price]',
    '[data-price]',
    '[class*="bundle-price"]',
    '[class*="bundle_price"]',
  ].join(',');

  const parseMoney = (value) => {
    if (!value) return null;
    const matches = String(value).match(/[0-9]+(?:[,.\u00a0\u202f ]+[0-9]{3})*(?:[,.][0-9]{1,2})?/g);
    if (!matches?.length) return null;

    const raw = matches[matches.length - 1]
      .replace(/[\u00a0\u202f ]/g, '')
      .replace(/,(?=\d{3}(?:\D|$))/g, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');
    const number = Number(raw);
    return Number.isFinite(number) ? number : null;
  };

  const formatBundleAmount = (value) => {
    const hasDecimals = Math.abs(value % 1) > Number.EPSILON;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(value);
  };

  const getActiveBundle = (root) => {
    const explicit = root.querySelector(ACTIVE_BUNDLE_SELECTOR);
    if (explicit) return explicit;

    const checked = root.querySelector('input[type="radio"]:checked');
    return checked?.closest('.lfcod-bundle-item, [data-quantity]') || null;
  };

  const getBundlePrice = (bundle) => {
    const priceNode = bundle.querySelector(BUNDLE_PRICE_SELECTOR);
    const directValue =
      priceNode?.dataset.bundlePrice ||
      priceNode?.dataset.price ||
      priceNode?.textContent;
    const renderedPrice = parseMoney(directValue);
    if (renderedPrice !== null) return renderedPrice;

    const quantity = Number(bundle.dataset.quantity);
    const discount = Number(bundle.dataset.discountValue);
    const type = bundle.dataset.discountType;
    const productPriceNode = document.querySelector(
      'product-info[id^="MainProduct"] [id^="price-"] .price__regular .soli-price-amount'
    );
    const basePrice = parseMoney(productPriceNode?.textContent);
    if (!Number.isFinite(quantity) || !Number.isFinite(basePrice)) return null;

    const subtotal = basePrice * quantity;
    if (!Number.isFinite(discount)) return subtotal;
    if (type === 'percentage' || type === 'percent') return subtotal * (1 - discount / 100);
    if (type === 'price') return Math.max(0, subtotal - discount);
    return subtotal;
  };

  const syncBundlePrice = (bundleOverride = null) => {
    const roots = document.querySelectorAll(BUNDLE_ROOT_SELECTOR);
    if (!roots.length) return;

    let activeBundle = bundleOverride;
    if (!activeBundle) {
      for (const root of roots) {
        activeBundle = getActiveBundle(root);
        if (activeBundle) break;
      }
    }
    if (!activeBundle) return;

    const price = getBundlePrice(activeBundle);
    if (!Number.isFinite(price)) return;
    const formatted = formatBundleAmount(price);

    document.querySelectorAll('product-info[id^="MainProduct"] [id^="price-"]').forEach((priceRoot) => {
      priceRoot.querySelectorAll(
        '.price__regular .price-item--regular .soli-price-amount, .price__sale .price-item--sale .soli-price-amount'
      ).forEach((node) => {
        if (node.textContent !== formatted) node.textContent = formatted;
      });
      priceRoot.dataset.soliBundlePrice = String(price);
    });
  };

  let bundleFrame = 0;
  const scheduleBundlePriceSync = () => {
    cancelAnimationFrame(bundleFrame);
    bundleFrame = requestAnimationFrame(() => {
      bundleFrame = 0;
      syncBundlePrice();
    });
  };

  const start = () => {
    document.querySelectorAll(TARGET_SELECTOR).forEach(replaceWithin);
    scheduleBundlePriceSync();

    const pending = new Set();
    let frame = 0;
    const flush = () => {
      frame = 0;
      pending.forEach(localizeNode);
      pending.clear();
      scheduleBundlePriceSync();
    };

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => pending.add(node)));
      if (!frame) frame = requestAnimationFrame(flush);
    }).observe(document.body, { childList: true, subtree: true });

    new MutationObserver(scheduleBundlePriceSync).observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'aria-selected', 'checked', 'data-price', 'data-bundle-price'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    document.addEventListener('click', (event) => {
      const root = event.target.closest(BUNDLE_ROOT_SELECTOR);
      if (!root) return;
      const clickedBundle = event.target.closest('.lfcod-bundle-item, [data-quantity]');
      if (clickedBundle && root.contains(clickedBundle)) {
        syncBundlePrice(clickedBundle);
      } else {
        scheduleBundlePriceSync();
      }
    });
    document.addEventListener('change', (event) => {
      if (event.target.closest(BUNDLE_ROOT_SELECTOR)) scheduleBundlePriceSync();
    });
    document.addEventListener('shopify:section:load', scheduleBundlePriceSync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
